import { Button } from "@/design-system/Button/Button";
import { Check } from "@/design-system/Check/Check";
import { Dialog } from "@/design-system/Dialog/Dialog";
import type { IRootState } from "@/redux/store";
import {
  dailyLimitNoun,
  dailyLimitVerb,
  outOfCreditsLine,
} from "@repo/shared/credits";
import { formatResetCountdown, resetTooltip } from "@repo/shared/credits";
import { Trans, useTranslation } from "@repo/shared/i18n";
import type { CreditFeatureId, OutOfCreditsReason } from "@repo/shared/redux";
import { dailyLimitSelector, dailyResetsAtSelector } from "@repo/shared/redux";
import { UPGRADE_BENEFITS } from "@repo/shared/subscription";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";

import { CreditIcon } from "./CreditIcon";
import { EverythingElseFree } from "./EverythingElseFree";
import { useCreditDialogTracking } from "./useCreditDialogTracking";
import { useNow } from "./useNow";

export type OutOfCreditsData = {
  /** The feature refused or charged. Absent when the URL matched no endpoint. */
  featureId?: CreditFeatureId;
  /** Defaults to a refusal; "spent" when the call went through and emptied it. */
  reason?: OutOfCreditsReason;
};

/** Shown when a user with a club has spent their balance. */
export const OutOfCreditsDialog = ({
  data,
  onClose,
}: {
  data?: OutOfCreditsData;
  onClose: () => void;
}) => {
  const { t } = useTranslation("subscription");
  const navigate = useNavigate();
  const featureId = data?.featureId;
  const reason = data?.reason ?? "refused";
  const resetsAt = useSelector(dailyResetsAtSelector);
  const dailyLimit = useSelector(dailyLimitSelector);
  const cost = useSelector((state: IRootState) =>
    featureId ? (state.permissionsReducer.featureCosts[featureId] ?? 0) : 0,
  );
  const now = useNow();
  const { trackExit } = useCreditDialogTracking({
    context: "out-of-credits",
    featureId,
    reason,
  });

  const goToPricingPage = () => {
    trackExit("positive");
    navigate("/subscription", { state: { from: "credits" } });
    onClose();
  };

  /*
    The heading is the Dialog's own title rather than a line inside the body.
    With an empty title the header row still rendered — an empty text block next
    to the close button — which is what left the X floating in a band of its
    own, and what the -mt-2 on the body was compensating for.
  */
  return (
    <Dialog
      isOpen
      onClose={() => {
        trackExit("negative");
        onClose();
      }}
      /*
        The coin belongs on the heading, not floating above the body — alone in
        the left column it read as a stray dot with nothing to attach to.
      */
      title={
        <span className="flex items-center gap-x-2">
          <CreditIcon size="large" />
          {t("credits.outOfCredits.title")}
        </span>
      }
      width={520}
      negLabel=""
      posLabel=""
    >
      <div className="flex flex-col gap-5 mobile:flex-row">
        {/* Column, so the reassurance can be pushed to the foot with mt-auto —
            it answers a different question from the two lines above it, and
            running straight on from them read as one undifferentiated block. */}
        <div className="flex flex-1 flex-col">
          <p>{outOfCreditsLine(reason, featureId, cost)}</p>
          <p className="mt-1.5">
            <Trans
              ns="subscription"
              i18nKey="credits.outOfCredits.refreshesIn"
              values={{
                noun: dailyLimitNoun(dailyLimit),
                verb: dailyLimitVerb(
                  dailyLimit,
                  t("credits.outOfCredits.verbRefreshes"),
                  t("credits.outOfCredits.verbRefresh"),
                ),
                when: resetsAt
                  ? formatResetCountdown(resetsAt, now)
                  : t("credits.outOfCredits.aFewHours"),
              }}
              components={{
                when: resetsAt ? (
                  <span
                    title={resetTooltip(resetsAt)}
                    className="cursor-help border-b border-dotted border-amber-400/60 text-amber-400"
                  />
                ) : (
                  <></>
                ),
              }}
            />
          </p>
          <EverythingElseFree className="mt-auto pt-6 text-xs" />
        </div>

        <div className="flex-1 rounded-xl bg-panel-middleground p-4.5">
          <div className="mb-3 text-[13px] font-bold text-foreground">
            {t("credits.outOfCredits.getUnlimited")}
          </div>
          {/* Same tick as the pricing page's plan cards, so the two surfaces
              make the same promise in the same hand. */}
          <ul className="mb-4.5 flex flex-col gap-2.5 text-xs">
            {UPGRADE_BENEFITS.map((benefit) => (
              <li key={benefit} className="flex items-start gap-2.5">
                <Check />
                {benefit}
              </li>
            ))}
          </ul>
          {/* The `from` state lands on the credits explainer rather than the
              top of the pricing page. */}
          <Button
            variant="primary"
            size="medium"
            stretch
            onClick={goToPricingPage}
          >
            {t("ultimatePlan.goUnlimited")}
          </Button>
          <button
            onClick={() => {
              trackExit("negative");
              onClose();
            }}
            className="mt-3 w-full text-center text-xs text-dark-tertiary"
          >
            {t("credits.outOfCredits.maybeLater")}
          </button>
        </div>
      </div>
    </Dialog>
  );
};
